import { useState, useCallback } from 'react';
import { LayoutDashboard } from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart, Line, Bar,
  AreaChart, Area,
  BarChart,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth,
  fmtDate, fmtAmt, fmtPct, fmtByFormat,
  Delta, KpiTile, UnavailableMetrics, ExceptionPanel, Section, EmptyState,
  CHART_COLOURS,
} from '@/components/analytics/AnalyticsKit';
import type {
  ExecutiveDashboardDto,
  ExecutiveDashboardFilter,
  BranchProfit,
} from '@/types/analytics.types';

// ── Chart helpers ──────────────────────────────────────────────────────────────

/** Compact axis labels — a Y axis full of 1,250,000.00 is unreadable. */
function axisAmt(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return v.toFixed(0);
}

const AXIS_TICK = { fontSize: 11 };
const GRID_CLS = 'stroke-gray-200 dark:stroke-gray-700';

const TREND_MONTH_OPTIONS = [6, 12, 24, 36];

// ── Excel export ───────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Section', header: 'Section', width: 14 },
  { key: 'Item', header: 'Item', width: 34 },
  { key: 'Value', header: 'Value', width: 18 },
  { key: 'Status', header: 'Status', width: 12 },
  { key: 'Change %', header: 'Change %', width: 12, numFmt: '#,##0.00' },
  { key: 'Revenue', header: 'Revenue', width: 16, numFmt: '#,##0.00' },
  { key: 'Expenses', header: 'Expenses', width: 16, numFmt: '#,##0.00' },
  { key: 'Net Profit', header: 'Net Profit', width: 16, numFmt: '#,##0.00' },
  { key: 'Net Margin %', header: 'Net Margin %', width: 14, numFmt: '#,##0.00' },
];

function buildExcelRows(data: ExecutiveDashboardDto): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];

  for (const k of data.kpis) {
    const unavailable = k.value === null || k.status === 'NO_DATA';
    rows.push({
      Section: 'KPI',
      Item: k.label,
      // Never export an unavailable metric as 0 — that would read as a measured zero.
      Value: unavailable ? 'Unavailable' : k.value,
      Status: k.status,
      'Change %': k.changePercent ?? '',
      Revenue: '',
      Expenses: '',
      'Net Profit': '',
      'Net Margin %': '',
    });
  }

  for (const b of data.profitByBranch) {
    rows.push({
      Section: 'Branch',
      Item: `${b.locationCode} — ${b.locationName}`,
      Value: '',
      Status: '',
      'Change %': b.growthPercent ?? '',
      Revenue: b.revenue,
      Expenses: b.expenses,
      'Net Profit': b.netProfit,
      'Net Margin %': b.netMarginPercent,
    });
  }

  if (data.unavailableMetrics.length > 0) {
    for (const m of data.unavailableMetrics) {
      rows.push({
        Section: 'Not available',
        Item: m,
        Value: 'Unavailable',
        Status: 'NO_DATA',
        'Change %': '',
        Revenue: '',
        Expenses: '',
        'Net Profit': '',
        'Net Margin %': '',
      });
    }
  }

  return rows;
}

// ── Component ──────────────────────────────────────────────────────────────────

export const ExecutiveDashboardPage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<ExecutiveDashboardFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    trendMonths: 24,
  });
  const [data, setData] = useState<ExecutiveDashboardDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getExecutiveDashboard(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  if (!hasPermission(Permissions.RPT_EXEC_DASHBOARD)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Executive Dashboard',
      columns: EXCEL_COLUMNS,
      rows: buildExcelRows(data),
      title: 'Executive Dashboard',
      subtitle: `${data.companyName} · ${filter.fromDate} to ${filter.toDate}`,
    }, `ExecutiveDashboard_${filter.fromDate}_${filter.toDate}`);
  };

  const handlePrint = () => setShowPrint(true);

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LayoutDashboard size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Executive Dashboard</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={handlePrint}
          hasData={!!data}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3 shrink-0')}>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Trend Months</label>
            <select
              value={filter.trendMonths ?? 24}
              onChange={e => setFilter(f => ({ ...f, trendMonths: Number(e.target.value) }))}
              className={INPUT_CLS}
            >
              {TREND_MONTH_OPTIONS.map(m => (
                <option key={m} value={m}>{m} months</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id || undefined }))}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <RunReportButton
            onClick={() => void run()}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to generate the Executive Dashboard.' />
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* Report meta */}
          <div className={cn(CARD_CLS, 'px-4 py-3')}>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Executive Dashboard — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              {' · '}Compared with {fmtDate(data.previousFromDate)} to {fmtDate(data.previousToDate)}
              {' · '}Generated {fmtDate(data.generatedAt)}
            </div>
          </div>

          {/* KPI grid */}
          {data.kpis.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">
              {data.kpis.map(k => (
                <KpiTile key={k.key} kpi={k} />
              ))}
            </div>
          )}

          {/* Revenue / Expenses / Net Profit trend */}
          <Section
            title="Revenue, Expenses & Net Profit"
            subtitle={`Last ${filter.trendMonths ?? 24} months`}
          >
            {data.trend.length === 0 ? (
              <EmptyState message="No trend data for this period." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={data.trend} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" className={GRID_CLS} />
                  <XAxis dataKey="period" tick={AXIS_TICK} />
                  <YAxis tick={AXIS_TICK} tickFormatter={axisAmt} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue" name="Revenue" fill={CHART_COLOURS[0]} barSize={14} />
                  <Bar dataKey="operatingExpenses" name="Operating Expenses" fill={CHART_COLOURS[3]} barSize={14} />
                  <Line
                    type="monotone"
                    dataKey="netProfit"
                    name="Net Profit"
                    stroke={CHART_COLOURS[1]}
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* Cash flow trend */}
          <Section title="Net Cash Movement" subtitle="Cash in less cash out, by period">
            {data.trend.length === 0 ? (
              <EmptyState message="No cash movement data for this period." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.trend} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <defs>
                    <linearGradient id="cashFlowFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLOURS[5]} stopOpacity={0.7} />
                      <stop offset="95%" stopColor={CHART_COLOURS[5]} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className={GRID_CLS} />
                  <XAxis dataKey="period" tick={AXIS_TICK} />
                  <YAxis tick={AXIS_TICK} tickFormatter={axisAmt} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="netCashMovement"
                    name="Net Cash Movement"
                    stroke={CHART_COLOURS[5]}
                    strokeWidth={2}
                    fill="url(#cashFlowFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* Distribution charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Section title="Top Expense Categories">
              {data.topExpenseCategories.length === 0 ? (
                <EmptyState message="No expense categories for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={data.topExpenseCategories}
                    layout="vertical"
                    margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className={GRID_CLS} />
                    <XAxis type="number" tick={AXIS_TICK} tickFormatter={axisAmt} />
                    <YAxis type="category" dataKey="label" tick={AXIS_TICK} width={140} />
                    <Tooltip />
                    <Bar dataKey="amount" name="Amount" fill={CHART_COLOURS[3]} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>

            <Section title="Cost Centre Distribution">
              {data.costCentreDistribution.length === 0 ? (
                <EmptyState message="No cost centre activity for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.costCentreDistribution}
                      dataKey="amount"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {data.costCentreDistribution.map((slice, i) => (
                        <Cell
                          key={slice.id ?? slice.code ?? String(i)}
                          fill={CHART_COLOURS[i % CHART_COLOURS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Section>
          </div>

          {/* Profit by branch */}
          <Section title="Profit by Branch">
            {data.profitByBranch.length === 0 ? (
              <EmptyState message="No branch profit data for this period." />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.profitByBranch} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" className={GRID_CLS} />
                    <XAxis dataKey="locationName" tick={AXIS_TICK} />
                    <YAxis tick={AXIS_TICK} tickFormatter={axisAmt} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="netProfit" name="Net Profit" fill={CHART_COLOURS[1]} barSize={28} />
                  </BarChart>
                </ResponsiveContainer>

                <div className="overflow-auto mt-3">
                  <table className="w-full text-sm border-collapse min-w-[720px]">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className={TH_CLS}>Branch</th>
                        <th className={TH_R_CLS}>Revenue</th>
                        <th className={TH_R_CLS}>Expenses</th>
                        <th className={TH_R_CLS}>Net Profit</th>
                        <th className={TH_R_CLS}>Net Margin</th>
                        <th className={TH_R_CLS}>Growth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.profitByBranch.map((b: BranchProfit, i: number) => (
                        <tr
                          key={b.locationId ?? `${b.locationCode}-${i}`}
                          className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {b.locationName}
                            <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">({b.locationCode})</span>
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(b.revenue)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(b.expenses)}</td>
                          <td className={cn(AMT_CLS, 'font-medium text-gray-900 dark:text-gray-100')}>{fmtAmt(b.netProfit)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtPct(b.netMarginPercent)}</td>
                          <td className="px-3 py-2 text-right">
                            <Delta percent={b.growthPercent} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Section>

          {/* Exceptions */}
          <Section title="Exceptions" subtitle="Items flagged for management attention">
            <ExceptionPanel items={data.exceptions} />
          </Section>

          {/* Metrics the backend declined to compute */}
          <UnavailableMetrics metrics={data.unavailableMetrics} />
        </div>
      )}

      {/* Print preview */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Executive Dashboard"
          orientation="portrait"
        >
          <ReportHeader
            reportTitle="Executive Dashboard"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period', value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Compared with', value: `${fmtDate(data.previousFromDate)} to ${fmtDate(data.previousToDate)}` },
            ]}
          />

          <div style={{ fontSize: '11px', fontWeight: 700, margin: '8px 0 4px' }}>Key Performance Indicators</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
            <thead>
              <tr>
                {['KPI', 'Value', 'Change %', 'Status'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      background: '#1e3a5f', color: 'white', padding: '5px 6px',
                      textAlign: i === 0 ? 'left' : 'right', fontWeight: 700, fontSize: '9px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.kpis.map(k => {
                const unavailable = k.value === null || k.status === 'NO_DATA';
                return (
                  <tr key={k.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '3px 6px' }}>{k.label}</td>
                    <td style={{
                      padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace',
                      color: unavailable ? '#9ca3af' : '#111827',
                    }}>
                      {unavailable ? 'Unavailable' : fmtByFormat(k.value, k.format)}
                    </td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {k.changePercent === null ? '—' : fmtPct(k.changePercent)}
                    </td>
                    <td style={{ padding: '3px 6px', textAlign: 'right', color: '#6b7280' }}>{k.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {data.profitByBranch.length > 0 && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, margin: '14px 0 4px' }}>Profit by Branch</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                <thead>
                  <tr>
                    {['Branch', 'Revenue', 'Expenses', 'Net Profit', 'Net Margin %'].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          background: '#1e3a5f', color: 'white', padding: '5px 6px',
                          textAlign: i === 0 ? 'left' : 'right', fontWeight: 700, fontSize: '9px',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.profitByBranch.map((b: BranchProfit, i: number) => (
                    <tr key={b.locationId ?? `${b.locationCode}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '3px 6px' }}>{b.locationCode} — {b.locationName}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(b.revenue)}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(b.expenses)}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtAmt(b.netProfit)}</td>
                      <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtPct(b.netMarginPercent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {data.unavailableMetrics.length > 0 && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, margin: '14px 0 4px' }}>Not shown on this report</div>
              <ul style={{ paddingLeft: '16px', margin: 0 }}>
                {data.unavailableMetrics.map(m => (
                  <li key={m} style={{ fontSize: '9px', color: '#6b7280' }}>{m}</li>
                ))}
              </ul>
            </>
          )}

          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
